import {
  bpToCommissionPercent,
  fromMinorUnits,
  levelToRole,
  nodeSharePoints,
  type HierarchyNode,
  type Role,
} from '@/features/hierarchy/hierarchy'

export type MemberAccountStatus = 'active' | 'inactive' | 'suspended' | 'closed'

/** Thin Account shape for Member 360 tabs (HierarchyNode view). */
export type MemberAccount = {
  id: string
  username: string
  displayName: string
  role: Role
  parentId: string | null
  path: string[]
  status: MemberAccountStatus
  effectiveStatus: MemberAccountStatus
  createdAt: string
  lastActiveAt: string | null
  currencyType?: string
  creditLimit: number
  balance: number
  distributedBalance: number
  exposure: number
  exposureStale?: boolean
  /** Lab Balance product NE (major). Players only when Agent-API ships them. */
  exposureSportsbook?: number
  exposureLiveCasino?: number
  exposureLiveCards?: number
  exposurePremiumBm?: number
  exposureHorseRacing?: number
  settlement: number
  share: { sportsbook: number; casino: number }
  commission: { fancy: number; bookmaker: number; matchOdds: number }
  betLimits: { min: number; max: number }
  memberCount: number
  directChildren: number
}

function statusOf(raw: string | undefined): MemberAccountStatus {
  const s = (raw ?? 'active').toLowerCase()
  if (s === 'inactive' || s === 'suspended' || s === 'closed') return s
  return 'active'
}

/**
 * Map hierarchy tree nodes into the Member 360 account view.
 * Money fields are major units (same as Agent-UI Account).
 */
export function memberAccountFromHierarchy(
  node: HierarchyNode,
  tree: HierarchyNode[],
  rootId: string,
): MemberAccount {
  const byId = new Map(tree.map((n) => [n.id, n]))
  const path: string[] = []
  const seen = new Set<string>()
  let cursor = node.parentId
  while (cursor && !seen.has(cursor)) {
    seen.add(cursor)
    path.unshift(cursor)
    if (cursor === rootId) break
    cursor = byId.get(cursor)?.parentId ?? null
  }

  const children = tree.filter((n) => n.parentId === node.id)
  const descendants = tree.filter((n) => {
    if (n.id === node.id) return false
    let c = n.parentId
    const walk = new Set<string>()
    while (c && !walk.has(c)) {
      if (c === node.id) return true
      walk.add(c)
      c = byId.get(c)?.parentId ?? null
    }
    return false
  })

  const creditLimit = fromMinorUnits(node.creditLimit)
  const balance = fromMinorUnits(node.liveBalance ?? node.balance)
  const distributedBalance = fromMinorUnits(node.distributedBalance)
  const exposure = fromMinorUnits(node.exposure)
  const settlement = fromMinorUnits(node.postedPnl)
  const status = statusOf(node.status)
  const share = nodeSharePoints(node)
  const productMinor = (v: string | undefined) =>
    v === undefined ? undefined : fromMinorUnits(v)

  return {
    id: node.id,
    username: node.username || node.name,
    displayName: node.name,
    role: levelToRole(node.level),
    parentId: node.parentId,
    path,
    status,
    effectiveStatus: status,
    createdAt: '',
    lastActiveAt: null,
    currencyType: node.currencyType,
    creditLimit,
    balance,
    distributedBalance,
    exposure,
    exposureStale: node.exposureStale,
    exposureSportsbook: productMinor(node.exposureSportsbook),
    exposureLiveCasino: productMinor(node.exposureLiveCasino),
    exposureLiveCards: productMinor(node.exposureLiveCards),
    exposurePremiumBm: productMinor(node.exposurePremiumBm),
    exposureHorseRacing: productMinor(node.exposureHorseRacing),
    settlement,
    share,
    commission: {
      fancy: bpToCommissionPercent(node.commissionBp ?? 0),
      bookmaker: bpToCommissionPercent(node.commissionBookmakerBp ?? 0),
      matchOdds: bpToCommissionPercent(node.commissionMatchOddsBp ?? 0),
    },
    betLimits: {
      min: fromMinorUnits(node.betMin),
      max: fromMinorUnits(node.betMax),
    },
    memberCount: descendants.length,
    directChildren: children.length,
  }
}

/** 7×24 zeros — Heatmap furniture until activity matrix is wired. */
export function emptyActivityMatrix(): number[][] {
  return Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => 0))
}

export type MemberDetailView = {
  account: MemberAccount
  upline: MemberAccount[]
  openExposure: number
  /** Soft-empty until analytics detail merges in. */
  daily: Array<{ date: string; turnover: number; memberPnl: number; bets: number }>
  bySport: Array<{ sport: string; turnover: number; memberPnl: number; bets: number }>
  activity: number[][]
  stakeBuckets: Array<{ label: string; count: number }>
  lifetime: {
    turnover: number
    memberPnl: number
    commission: number
    bets: number
    settledBets: number
    wins: number
    avgStake: number
    biggestWin: number
    biggestLoss: number
  }
}

/** Build Member 360 detail from hierarchy tree + agent node (no analytics). */
export function buildMemberDetailView(
  tree: HierarchyNode[],
  node: HierarchyNode,
  rootId: string,
): MemberDetailView | null {
  const byId = new Map(tree.map((n) => [n.id, n]))
  if (!byId.has(node.id)) {
    tree = [...tree, node]
  } else {
    tree = tree.map((n) => (n.id === node.id ? { ...n, ...node } : n))
  }
  const account = memberAccountFromHierarchy(
    tree.find((n) => n.id === node.id) ?? node,
    tree,
    rootId,
  )
  const upline: MemberAccount[] = []
  const seen = new Set<string>()
  let cursor = account.parentId
  while (cursor && !seen.has(cursor)) {
    seen.add(cursor)
    const parent = tree.find((n) => n.id === cursor)
    if (!parent) break
    upline.unshift(memberAccountFromHierarchy(parent, tree, rootId))
    cursor = parent.parentId
  }
  return {
    account,
    upline,
    openExposure: account.exposure,
    daily: [],
    bySport: [],
    activity: emptyActivityMatrix(),
    stakeBuckets: [],
    lifetime: {
      turnover: 0,
      memberPnl: 0,
      commission: 0,
      bets: 0,
      settledBets: 0,
      wins: 0,
      avgStake: 0,
      biggestWin: 0,
      biggestLoss: 0,
    },
  }
}
